import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FileText,
  Calculator,
  Monitor,
  BookOpen,
  Lightbulb,
  Shield,
  Globe,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import { services } from "@/data";
import { JSX } from "react/jsx-runtime";

export const Route = createFileRoute("/services/")({
  component: ServicesPage,
});

const iconMap: Record<string, React.ElementType> = {
  FileText,
  Calculator,
  Monitor,
  BookOpen,
  Lightbulb,
  Shield,
  Globe,
  GraduationCap,
};

// eslint-disable-next-line react-refresh/only-export-components
function ServicesPage(): JSX.Element {
  return (
    <>
      <Helmet>
        <title>Tax Services | Rosewood Tax Consultancy Kenya</title>
        <meta
          name="description"
          content="Comprehensive tax services in Kenya: KRA registration, tax returns, eTIMS compliance, bookkeeping, tax advisory, audit defence, international tax, and training."
        />
      </Helmet>

      <section className="bg-[#FAF7F2] pt-16 pb-14">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >

            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-4">
              Every Tax Need, <span className=" text-[#6B1E2A]">Covered</span>
            </h1>
            <p className="text-lg text-[#6B6B6B] max-w-2xl mx-auto">
              From basic KRA compliance to complex international tax structures
              — our eight service lines cover every dimension of your tax life.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, i) => {
              const Icon = iconMap[service.icon] || FileText;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                >
                  <Link
                    to={`/services/${service.slug}` as never}
                    className="group flex gap-6 p-7 bg-[#FAF7F2] rounded-xl hover:-translate-y-0.5 transition-all h-full"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-[#6B1E2A]/10 group-hover:bg-[#6B1E2A] rounded-xl flex items-center justify-center transition-colors">
                        <Icon
                          size={24}
                          className="text-[#6B1E2A] group-hover:text-white transition-colors"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h2 className="font-semibold text-[#1A1A1A] text-xl mb-2 group-hover:text-[#6B1E2A] transition-colors">
                        {service.title}
                      </h2>
                      <p className="text-[#6B6B6B] text-sm leading-relaxed mb-4">
                        {service.shortDescription}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {service.features.slice(0, 3).map((feature) => (
                          <span
                            key={feature}
                            className="px-2.5 py-1 bg-white border border-[#E8E0D0] text-xs text-[#6B6B6B] rounded-md"
                          >
                            {feature}
                          </span>
                        ))}
                        {service.features.length > 3 && (
                          <span className="px-2.5 py-1 bg-white border border-[#E8E0D0] text-xs text-[#6B6B6B] rounded-md">
                            +{service.features.length - 3} more
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-[#6B1E2A] group-hover:text-[#4A1219]">
                        View Service Details
                        <ArrowRight
                          size={14}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

    </>
  );
}
